import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { SyncMap } from "twilio-sync";
import { Client, Conversation, Participant, Message, User } from "@twilio/conversations";
import { GenericThemeShape } from "@twilio-paste/theme";
import { AlertVariants } from "@twilio-paste/core/alert";

import { FileAttachmentConfig, TranscriptConfig } from "../definitions";
import { PresenceVisitor } from "../types/Glance";

export enum EngagementPhase {
    PreEngagementForm = "PreEngagementForm",
    MessagingCanvas = "MessagingCanvas",
    PostChatSurvey = "PostChatSurvey",
    Loading = "Loading",
    ThankYouPhase = "ThankYouPhase"
}

export type ChatState = {
    conversationsClient?: Client;
    conversation?: Conversation;
    participants?: Participant[];
    users?: User[];
    messages?: Message[];
    attachedFiles?: File[];
    conversationState?: string;
};

type FieldType = "email" | "text" | "phone" | "textArea" | "dropdown" | "checkbox";

export type Field = {
    type: FieldType;
    label: string;
    name: string;
    required?: boolean;
    placeholder?: string;
    options?: Record<string, string>;
};

export type PreEngagementData = {
    name: string;
    email: string;
    fields?: Record<string, string>;
    query: string;
};

/* eslint-disable import/no-unused-modules */
export type SyncMapItemTask = {
    date: string;
    sid: string;
    workerName: string;
    workerSid: string;
};

export type SyncMapItemData = {
    preEngagementForm?: Record<string, string>;
    tasks?: SyncMapItemTask[];
    isCoBrowserEnabled?: boolean;
};

export type SessionState = {
    currentPhase: EngagementPhase;
    expanded: boolean;
    token?: string;
    conversationSid?: string;
    conversationsClient?: Client;
    conversation?: Conversation;
    users?: User[];
    participants?: Participant[];
    messages?: Message[];
    conversationState?: "active" | "inactive" | "closed";
    preEngagementData?: PreEngagementData;
    syncMapItemData?: SyncMapItemData;
    syncMap?: SyncMap;
};

export type GlanceState = {
    visitor?: PresenceVisitor;
    isConnected?: boolean;
};

export type GlanceConfiguration = {
    groupid: number;
    site: string;
};

export type ConfigState = {
    serverUrl?: string;
    apiKey?: string;
    theme?: {
        isLight?: boolean;
        overrides?: Partial<GenericThemeShape>;
    };
    fileAttachment?: FileAttachmentConfig;
    transcript?: TranscriptConfig;
    preEngagementFields?: Field[];
    postChatSurveyFields?: Field[];
    /**
     * This variable will remove the post chat survey
     * This is a new variable that will omit any change
     * on postChatSurveyFields. This is required to preserve
     * previous configurations that the email is required
     */
    disablePostChatSurvey?: boolean;
    glance?: GlanceConfiguration;
    autoInitConfig?: {
        [key: string]: string;
        name: string;
        email: string;
        query: string;
    };
};

export type Notification = {
    dismissible: boolean;
    id: string;
    onDismiss?: () => void;
    message: string;
    timeout?: number;
    type: AlertVariants;
};

export type NotificationState = Notification[];

export type AppState = {
    chat: ChatState;
    config: ConfigState;
    session: SessionState;
    notifications: NotificationState;
    glance: GlanceState;
};

export type AppDispatch = ThunkDispatch<AppState, unknown, AnyAction>;
